// ─── demandeMgd.js ───────────────────────────────────────────────────
// Lire la DEMANDE D'ANALYSES de MGD et la confronter au plan du SaaS.
//
// POURQUOI CE FICHIER EXISTE
//
// Le 25.08.2026, les factures du cycle de Clelia depassaient de 131.10 CHF
// ce que le SaaS avait prevu. On a d'abord accuse le labo. La demande
// d'analyses, lue une heure plus tard, a montre que les factures etaient
// justes au centime : Anissa avait coche sur le formulaire MGD trois
// analyses que le plan ignorait, et commande un CYCLE du cortisol la ou le
// plan portait un simple dosage. 108.60 des 131.10 venaient de la.
//
// Le plan du SaaS est une PREVISION. La demande d'analyses est la COMMANDE.
// Tant que personne ne compare les deux, l'ecart ne se decouvre qu'a la
// facture, six semaines plus tard, souvent apres qu'Anissa a deja facture
// sa cliente. C'est trop tard : elle avance les frais, donc l'ecart sort de
// sa poche.
//
// CE QUI REND LA LECTURE FIABLE
//
// MGD ecrit chaque ligne sous la forme « Libelle [CODE] » :
//   ELIPSEgenes [EUELI] · BILAN NUTRITIONNEL DE BASE [MGNUT]
//   Cycle du cortisol salivaire [CYCCOR] · Glucose a jeun [G]
// Ce n'est pas du texte a interpreter, c'est un identifiant. On rapproche
// sur `lab_tests.lab_code`, jamais sur le libelle.
//
// ⚠ UN CODE INCONNU EST UNE ALERTE, PAS UNE ABSENCE. Six analyses du
// catalogue n'ont pas encore de `lab_code` releve, et MGD en propose bien
// d'autres. Deviner une correspondance produirait un cout faux presente
// comme sur ; on prefere dire « je ne sais pas ce que c'est » et laisser
// Anissa trancher.

/** Lignes du formulaire qui ne sont pas des analyses facturables. */
export const CODES_NON_FACTURABLES = Object.freeze(['MATERIEL']);

/** Bornes de la section utile du formulaire. */
const DEBUT_SECTION = /ANALYSES\s+PRESCRITES/i;
const FIN_SECTION = /RENSEIGNEMENTS\s+CLINIQUES|TRANSMISSION\s+DES\s+R[EÉ]SULTATS/i;

// Un code porte au moins une lettre. Sans cette exigence, le numero de
// prescripteur « DEROUBAIX Anissa [13878] » passerait pour une analyse.
const JETON_CODE = /\[\s*([A-Z][A-Z0-9_]*|[A-Z0-9_]*[A-Z][A-Z0-9_]*)\s*\]/g;

/** Numero de demande, celui du code-barres. Il relie la demande a la facture. */
const NUMERO_DEMANDE = /No\s*demande[\s\S]{0,80}?(\d{6,10})/i;

function sectionAnalyses(texte) {
  const debut = texte.search(DEBUT_SECTION);
  if (debut === -1) return null;
  const reste = texte.slice(debut);
  const fin = reste.search(FIN_SECTION);
  return fin === -1 ? reste : reste.slice(0, fin);
}

/**
 * Extrait les codes d'analyses d'une demande MGD.
 *
 * @param {string} texte - texte brut du PDF, dans n'importe quel ordre de
 *   colonnes : on ne s'appuie pas sur la mise en page, seulement sur les
 *   jetons entre crochets.
 * @returns {{
 *   lisible: boolean,
 *   numeroDemande: string|null,
 *   codes: string[],
 *   nonFacturables: string[],
 * }}
 */
export function lireDemandeMgd(texte = '') {
  const brut = typeof texte === 'string' ? texte : '';
  const illisible = {
    lisible: false, numeroDemande: null, codes: [], nonFacturables: [],
  };

  const section = sectionAnalyses(brut);
  // Pas de section reconnue : on ne devine pas sur le document entier, on
  // dit que la lecture a echoue. Un faux positif ici ferait valider une
  // reconciliation batie sur rien.
  if (!section) return illisible;

  const trouves = [...section.matchAll(JETON_CODE)].map(m => m[1]);
  if (trouves.length === 0) return illisible;

  const vus = new Set();
  const codes = [];
  const nonFacturables = [];
  for (const c of trouves) {
    if (vus.has(c)) continue;
    vus.add(c);
    if (CODES_NON_FACTURABLES.includes(c)) nonFacturables.push(c);
    else codes.push(c);
  }

  const numero = brut.match(NUMERO_DEMANDE);
  return {
    lisible: true,
    numeroDemande: numero ? numero[1] : null,
    codes,
    nonFacturables,
  };
}

const nombre = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const arrondi2 = (n) => Math.round(n * 100) / 100;

/**
 * Confronte une demande lue au plan enregistre.
 *
 * @param {{codes: string[], lisible: boolean}} demande - sortie de lireDemandeMgd
 * @param {Array<{code?: string}>} planTests - `selected_tests` du plan
 * @param {Array<{code?: string, lab_code?: string, display_name?: string, cost_anissa_chf?: number|string, source_lab?: string}>} catalogue
 * @returns {{
 *   concordant: boolean,
 *   ajoutees: Array<object>,
 *   manquantes: Array<object>,
 *   inconnues: string[],
 *   communes: Array<object>,
 *   surcoutAjoute: number,
 * }}
 */
export function reconcilierDemande(demande, planTests = [], catalogue = []) {
  const vide = {
    concordant: false, ajoutees: [], manquantes: [], inconnues: [],
    communes: [], surcoutAjoute: 0,
  };
  if (!demande?.lisible || !Array.isArray(demande.codes)) return vide;

  const parLabCode = new Map();
  const parCode = new Map();
  for (const t of Array.isArray(catalogue) ? catalogue : []) {
    if (!t) continue;
    if (t.lab_code) parLabCode.set(String(t.lab_code), t);
    if (t.code) parCode.set(String(t.code), t);
  }

  const codesPlan = new Set(
    (Array.isArray(planTests) ? planTests : [])
      .map(t => t?.code).filter(Boolean).map(String)
  );

  const ajoutees = [];
  const communes = [];
  const inconnues = [];
  const commandes = new Set();

  for (const labCode of demande.codes) {
    const test = parLabCode.get(labCode);
    if (!test) {
      // ⚠ Ni ignore, ni devine. Anissa verra le code brut et decidera.
      inconnues.push(labCode);
      continue;
    }
    commandes.add(test.code);
    if (codesPlan.has(test.code)) communes.push(test);
    else ajoutees.push(test);
  }

  // Prevues mais pas commandees : souvent un renoncement en consultation.
  // A signaler quand meme, sinon le montant refacture reste trop haut.
  const manquantes = [...codesPlan]
    .filter(c => !commandes.has(c))
    .map(c => parCode.get(c) || { code: c, display_name: c });

  const surcoutAjoute = arrondi2(
    ajoutees.reduce((s, t) => s + nombre(t.cost_anissa_chf), 0)
  );

  return {
    // Concordant exige zero inconnue : tant qu'un code n'est pas identifie,
    // on ne peut pas affirmer que la demande correspond au plan.
    concordant: ajoutees.length === 0 && manquantes.length === 0 && inconnues.length === 0,
    ajoutees,
    manquantes,
    inconnues,
    communes,
    surcoutAjoute,
  };
}

/**
 * Statuts pendant lesquels le controle a encore une valeur.
 *
 * `draft` est exclu : elle compose encore, la commande n'est pas passee, et
 * un rappel a ce moment-la serait du bruit. `completed` aussi : la cliente
 * est facturee, le controle n'y changerait plus rien. Reste la fenetre qui
 * compte, entre la commande passee et la facture envoyee — exactement celle
 * ou le cycle de Clelia est passe sans que personne ne regarde.
 */
export const STATUTS_A_CONTROLER = Object.freeze(['sent', 'in_progress']);

const labosDuPlan = (plan) => new Set(
  (Array.isArray(plan?.selected_tests) ? plan.selected_tests : [])
    .map(t => t?.source_lab).filter(Boolean)
);

/**
 * Quels controles manquent encore sur ce plan.
 *
 * Les deux labos posent la meme question — « le plan decrit-il ce qui a
 * vraiment ete commande ? » — mais la reponse ne s'obtient pas pareil :
 *   mgd    : on relit la demande d'analyses, un PDF du portail.
 *   ortho  : il n'existe aucun document, les bons sont remplis a la main.
 *            Seule la declaration d'Anissa fait foi.
 *
 * D'ou deux marqueurs distincts. Un plan mixte porte vraiment deux
 * documents ; n'en cocher qu'un laisserait l'autre passer.
 *
 * @param {{status?: string, demande_controlee_le?: string|null, bons_confirmes_le?: string|null, selected_tests?: Array<object>}} plan
 * @returns {Array<'mgd'|'ortho'>}
 */
export function controlesManquants(plan) {
  if (!plan || !STATUTS_A_CONTROLER.includes(plan.status)) return [];
  const labos = labosDuPlan(plan);
  const manquants = [];
  if (labos.has('mgd') && !plan.demande_controlee_le) manquants.push('mgd');
  if (labos.has('ortho_analytic') && !plan.bons_confirmes_le) manquants.push('ortho');
  return manquants;
}

/**
 * Faut-il rappeler a Anissa de controler sa commande ?
 *
 * @param {object} plan
 * @returns {boolean}
 */
export function rappelControleNecessaire(plan) {
  return controlesManquants(plan).length > 0;
}

/**
 * Le titre du rappel, qui nomme ce qui manque.
 *
 * « La commande n'a pas ete controlee » sur un plan mixte laisserait croire
 * qu'un seul geste suffit. Il y a deux documents, on le dit.
 *
 * @param {Array<'mgd'|'ortho'>} manquants
 * @returns {string|null}
 */
export function titreRappelControle(manquants = []) {
  const mgd = manquants.includes('mgd');
  const ortho = manquants.includes('ortho');
  if (mgd && ortho) return 'La demande MGD et les bons Ortho n’ont pas été contrôlés';
  if (mgd) return 'La demande MGD n’a pas encore été contrôlée';
  if (ortho) return 'Les bons Ortho n’ont pas encore été confirmés';
  return null;
}

/**
 * Une phrase pour Anissa, ou null s'il n'y a rien a dire.
 * Le silence est reserve au cas ou tout concorde.
 */
export function formulerEcart(reconciliation) {
  if (!reconciliation || reconciliation.concordant) return null;
  const bouts = [];
  const { ajoutees, manquantes, inconnues, surcoutAjoute } = reconciliation;

  if (ajoutees.length) {
    bouts.push(
      `${ajoutees.length} analyse${ajoutees.length > 1 ? 's' : ''} commandée${ajoutees.length > 1 ? 's' : ''} `
      + `hors du plan (+${surcoutAjoute} CHF)`
    );
  }
  if (manquantes.length) {
    bouts.push(`${manquantes.length} prévue${manquantes.length > 1 ? 's' : ''} mais non commandée${manquantes.length > 1 ? 's' : ''}`);
  }
  if (inconnues.length) {
    bouts.push(`${inconnues.length} code${inconnues.length > 1 ? 's' : ''} que je ne reconnais pas (${inconnues.join(', ')})`);
  }
  return bouts.join(', ');
}
